import type { PointerEvent as ReactPointerEvent } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { IconCheckOutline18 as CheckIcon } from "nucleo-ui-outline-18"

import { cn, USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import {
	hexToHsv,
	hsvToHex,
	normalizeHex,
} from "@productlane/lib/src/color-hsv"
import { ProjectIcons } from "@productlane/ui"

export const PICKER_WIDTH = 376

const RAINBOW =
	"conic-gradient(from 90deg, #fb424c, #e8b600, #00b55c, #00b5ed, #5e6ad2, #f38e82, #fb424c)"

/** A filled circle, with a check when it is the chosen colour. */
function Swatch({
	color,
	selected,
	onClick,
	label,
}: {
	color: string
	selected: boolean
	onClick?: () => void
	label: string
}) {
	return (
		<button
			type="button"
			aria-label={label}
			aria-pressed={selected}
			onClick={onClick}
			className="flex size-[22px] flex-none cursor-default items-center justify-center rounded-full"
			style={{ background: color }}
		>
			{selected && (
				<CheckIcon className="size-3 flex-none text-white" aria-hidden />
			)}
		</button>
	)
}

/**
 * The colour row from Linear's picker: the palette, then a rainbow toggle that
 * swaps in a hex field and a saturation square for any other colour.
 */
export function ColorSection({
	color,
	onChange,
}: {
	color: string
	onChange: (color: string) => void
}) {
	const isPaletteColor = Object.values(USER_COLOR_PALETTE).some(
		({ hex }) => hex.toLowerCase() === color.toLowerCase(),
	)
	const [custom, setCustom] = useState(!isPaletteColor)
	const [draft, setDraft] = useState(color)
	const [hexText, setHexText] = useState(color.replace(/^#/, ""))

	useEffect(() => {
		setDraft(color)
		setHexText(color.replace(/^#/, ""))
	}, [color])

	const commit = (next: string) => {
		const hex = normalizeHex(next)
		if (!hex) {
			setHexText(color.replace(/^#/, ""))
			return
		}
		setDraft(hex)
		setHexText(hex.slice(1))
		if (hex !== color.toLowerCase()) onChange(hex)
	}

	return (
		<div className="flex flex-col gap-3 px-4 pt-4">
			<div className="flex h-[26px] items-center gap-2.5">
				{custom ? (
					<>
						<Swatch color={draft} selected label="Selected colour" />
						<label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
							<span className="flex-none text-tertiary">HEX</span>
							<span className="text-secondary">#</span>
							<input
								name="icon-picker-hex"
								value={hexText}
								maxLength={7}
								spellCheck={false}
								autoComplete="off"
								onChange={(event) => setHexText(event.target.value)}
								onBlur={() => commit(hexText)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault()
										commit(hexText)
									}
								}}
								className="-ml-2 min-w-0 flex-1 bg-transparent text-primary outline-none"
							/>
						</label>
					</>
				) : (
					<div className="flex flex-1 items-center gap-2.5">
						{Object.entries(USER_COLOR_PALETTE).map(([name, { hex }]) => (
							<Swatch
								key={hex}
								color={hex}
								label={name}
								selected={hex.toLowerCase() === color.toLowerCase()}
								onClick={() => commit(hex)}
							/>
						))}
					</div>
				)}
				<button
					type="button"
					aria-label="Custom colour"
					aria-pressed={custom}
					onClick={() => setCustom((open) => !open)}
					className={cn(
						"size-[22px] flex-none cursor-default rounded-full ring-offset-2 ring-offset-white dark:ring-offset-gray-850",
						custom ? "ring-2 ring-[#5e6ad2]" : "",
					)}
					style={{ background: RAINBOW }}
				/>
			</div>
			{custom && (
				<ColorSquare
					color={draft}
					onDraft={(next) => {
						setDraft(next)
						setHexText(next.slice(1))
					}}
					onCommit={commit}
				/>
			)}
		</div>
	)
}

function fraction(event: ReactPointerEvent<HTMLElement>) {
	const rect = event.currentTarget.getBoundingClientRect()
	return {
		x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
		y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
	}
}

/** Saturation and brightness on the square, hue on the bar; it saves when the drag ends. */
function ColorSquare({
	color,
	onDraft,
	onCommit,
}: {
	color: string
	onDraft: (color: string) => void
	onCommit: (color: string) => void
}) {
	const hsv = hexToHsv(color) ?? { h: 0, s: 0, v: 0 }
	const [hue, setHue] = useState(hsv.h)
	const latest = useRef(color)
	latest.current = color

	useEffect(() => {
		const next = hexToHsv(color)
		if (next && next.s > 0 && next.v > 0) setHue(next.h)
	}, [color])

	const drag = (
		read: (event: ReactPointerEvent<HTMLDivElement>) => string,
	) => ({
		onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => {
			event.currentTarget.setPointerCapture(event.pointerId)
			onDraft(read(event))
		},
		onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => {
			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				onDraft(read(event))
			}
		},
		onPointerUp: () => onCommit(latest.current),
	})

	const squareHandlers = drag((event) => {
		const { x, y } = fraction(event)
		return hsvToHex({ h: hue, s: x, v: 1 - y })
	})
	const hueHandlers = drag((event) => {
		const nextHue = fraction(event).y * 360
		setHue(nextHue)
		return hsvToHex({ h: nextHue, s: hsv.s || 1, v: hsv.v || 1 })
	})

	return (
		<div className="flex h-[100px] gap-3">
			<div
				{...squareHandlers}
				className="relative flex-1 touch-none rounded-md"
				style={{
					background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hsvToHex({ h: hue, s: 1, v: 1 })})`,
				}}
			>
				<div
					className="pointer-events-none absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
					style={{
						left: `${hsv.s * 100}%`,
						top: `${(1 - hsv.v) * 100}%`,
						background: color,
					}}
				/>
			</div>
			<div
				{...hueHandlers}
				className="relative w-2 flex-none touch-none rounded-full"
				style={{
					background:
						"linear-gradient(to bottom, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)",
				}}
			>
				<div
					className="pointer-events-none absolute left-1/2 h-1.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-gray-300 bg-white"
					style={{ top: `${(hue / 360) * 100}%` }}
				/>
			</div>
		</div>
	)
}

/** Linear's search field and 14-column grid, every icon in the chosen colour. */
export function IconGrid({
	selectedIcon,
	color,
	onPick,
}: {
	selectedIcon?: string | null
	color?: string
	onPick: (icon: string) => void
}) {
	const [query, setQuery] = useState("")
	const icons = useMemo(() => {
		const needle = query.trim().toLowerCase()
		return Object.entries(ProjectIcons).filter(([key]) =>
			key.toLowerCase().includes(needle),
		)
	}, [query])

	return (
		<div className="flex flex-col">
			<div className="mt-3 border-y border-gray-200 px-4 py-2.5 dark:border-gray-700/80">
				<input
					name="icon-search"
					value={query}
					placeholder="Search icons..."
					autoFocus
					autoComplete="off"
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={(event) => {
						const first = icons[0]
						if (event.key === "Enter" && first) {
							event.preventDefault()
							onPick(first[0])
						}
					}}
					className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
				/>
			</div>
			<div className="max-h-[240px] overflow-y-auto px-3 pb-3 pt-2">
				{icons.length > 0 ? (
					<div className="grid grid-cols-14">
						{icons.map(([key, Icon]) => (
							<button
								key={key}
								type="button"
								aria-label={key}
								onClick={() => onPick(key)}
								className={cn(
									"flex size-[25px] cursor-default items-center justify-center rounded-md text-[15px] hover:bg-gray-100 dark:hover:bg-gray-800",
									key === selectedIcon && "bg-gray-100 dark:bg-gray-800",
								)}
								style={{ color: color ?? undefined }}
							>
								<Icon className="flex-none" />
							</button>
						))}
					</div>
				) : (
					<p className="py-6 text-center text-sm text-tertiary">
						No icons found
					</p>
				)}
			</div>
		</div>
	)
}
